import { apiClient } from './client';

/**
 * Multi-participant Focus Overlay (MULTI-PARTICIPANT-FOCUS).
 *
 * One clinical event worked by several people wearing EEG at the same time.
 * The endpoint returns each recording's focus trace + pre-computed
 * peaks/valleys + team role + Muse signal health, on a shared wall-clock
 * window, so the portal can overlay them on one absolute time axis.
 *
 * Alignment note (mirrors the server): sample `i` sits at
 * `started_at + i * step_s` seconds, but `step_s` walks the *captured* EEG —
 * where the Muse dropped, `coverage_s < (ended_at - started_at)`, so a trace
 * can be shorter than its session. `eeg_status` / `coverage_s` let the UI
 * show a thin trace as thin rather than as low focus.
 */

export interface FocusExtremum {
  kind: 'peak' | 'valley';
  /** Seconds from the trace start (same base as the samples). */
  time_s: number;
  focus: number;
}

export interface FocusCaption {
  /** Seconds from the recording start (same base as the samples). */
  time_s: number;
  headline: string;
  transcript: string;
  summary: string;
}

export interface FocusMotion {
  phase: string | null;
  bimanual_dexterity: number | null;
  idle_time_fraction: number | null;
  left_path_length_cm: number | null;
  right_path_length_cm: number | null;
  total_path_length_cm: number | null;
  left_smoothness_ldlj: number | null;
  right_smoothness_ldlj: number | null;
  left_tremor_power: number | null;
  right_tremor_power: number | null;
}

export interface FocusParticipant {
  session_row_id: string;
  session_id: string;
  participant_user_id: string | null;
  participant_label: string | null;
  /** Team role on this case: lead_surgeon / resident / scrub_nurse / … */
  role: string | null;
  started_at: string;
  ended_at: string;
  phase: string | null;
  step_s: number | null;
  /** Per-sample traces; sample i is at `started_at + i * step_s`. */
  /** null marks a window with no usable EEG: a gap, not a low value. */
  focus: (number | null)[];
  engagement: (number | null)[];
  extrema: FocusExtremum[];
  /** AI-labelled transcript moments (headline + Whisper text), keyed by
   *  session-elapsed time_s — feeds the caption track. */
  transcript: FocusCaption[];
  coverage_s: number;
  eeg_hz: number | null;
  eeg_status: string | null;
  baseline_focus: number | null;
  motion: FocusMotion | null;
  /** Per-second angular-speed |ω| (deg/s), aligned like `focus`. Empty until
   *  the motion worker has (re-)scored the recording. */
  motion_trace: (number | null)[];
  motion_step_s: number | null;
  /** Video-prep progress while the uploaded clip is still being processed;
   *  null once ready (or no video). Drives the Analysis player's "preparing
   *  video" bar. */
  video_prep: VideoPrep | null;
}

export interface VideoPrep {
  /** Friendly stage, e.g. "Aligning audio", "Extracting video frames". */
  stage: string;
  done: number | null;
  total: number | null;
}

export interface FocusOverlay {
  instance_id: string;
  procedure_name: string;
  started_at: string | null;
  ended_at: string | null;
  window_start: string | null;
  window_end: string | null;
  participants: FocusParticipant[];
}

/** Overlay-ready focus payload for one procedure instance (case). */
export async function getFocusOverlay(
  tenantId: string,
  instanceId: string,
): Promise<FocusOverlay> {
  const { data } = await apiClient.get<FocusOverlay>(
    `/api/v1/tenants/${tenantId}/procedure-instances/${instanceId}/focus-overlay`,
  );
  return data;
}

/** Participant read-only: the overlay for one of the participant's own cases
 *  (same full-comparison payload; the gate is participation). */
export async function getMyFocusOverlay(instanceId: string): Promise<FocusOverlay> {
  const { data } = await apiClient.get<FocusOverlay>(
    `/api/v1/tenants/me/cases/${instanceId}/focus-overlay`,
  );
  return data;
}
